import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  Trash2,
  Package,
  FileDown,
  X,
} from "lucide-react";
import { ShipmentGroup, Shipment } from "../../types";
import { createGroupManifestPdf } from "../../utils/generateGroupManifest";
import { toast } from "react-toastify";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";
import { vehicleLabel } from "../../utils/vehicleLabel";

interface ShipmentGroupTableProps {
  groups: ShipmentGroup[];
  onView: (group: ShipmentGroup) => void;
  onEdit: (group: ShipmentGroup) => void;
  onDelete: (groupId: string) => void;
  loading: boolean;
}

export const ShipmentGroupTable: React.FC<ShipmentGroupTableProps> = ({
  groups,
  onEdit,
  onDelete,
  loading,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("shipmentGroup");

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isManifestPreviewOpen, setIsManifestPreviewOpen] = useState(false);
  const [manifestPreviewUrl, setManifestPreviewUrl] = useState<string | null>(
    null,
  );
  const [_, setPreviewGroupId] = useState<string | null>(null);

  const toggleExpanded = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleShipmentClick = async (shipmentId: string) => {
    if (!shipmentId) return;
    try {
      navigate(`/shipments/${shipmentId}`);
    } catch (error) {
      console.error("Error loading shipment:", error);
      toast.error(t("toast.shipmentLoadFailed"));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  };

  const getShipmentStatusColor = (status: string) => {
    const colors = {
      Order: "bg-gray-100 text-gray-800",
      ReadyForPickup: "bg-purple-100 text-purple-800",
      AwaitingPickup: "bg-orange-100 text-orange-800",
      InTransit: "bg-blue-100 text-blue-800",
      Completed: "bg-green-100 text-green-800",
    };
    return colors[status as keyof typeof colors] || colors.Order;
  };

  function toSentenceCase(text: string) {
    if (!text) return "";
    text = text.toLowerCase();
    return text.replace(/([a-zA-Z])/, (match) => match.toUpperCase());
  }

  const getGroupTotalWeight = (group: ShipmentGroup) => {
    if (!group.shipments?.length) return 0;

    return group.shipments.reduce((total, groupShipment) => {
      const shipment =
        typeof groupShipment.shipment === "object"
          ? groupShipment.shipment
          : null;

      return total + (shipment?.totalWeight || 0);
    }, 0);
  };

  const handlePreviewManifest = async (group: ShipmentGroup) => {
    try {
      setPreviewGroupId(group._id);
      setIsManifestPreviewOpen(true);

      const pdfBytes = await createGroupManifestPdf(group);
      const safeBytes = new Uint8Array(pdfBytes);
      const blob = new Blob([safeBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      if (manifestPreviewUrl) {
        URL.revokeObjectURL(manifestPreviewUrl);
      }

      setManifestPreviewUrl(url);
    } catch (error) {
      console.error("Failed to generate manifest preview:", error);
      toast.error(t("toast.manifestFailed"));
      setIsManifestPreviewOpen(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Domestic":
        return "bg-blue-100 text-blue-800";
      case "International":
        return "bg-purple-100 text-purple-800";
      case "Mixed":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isAssignedToDriverApp = (
    groupedShipments: { shipment: Shipment }[],
  ) => {
    return (
      groupedShipments.length > 0 &&
      groupedShipments.every((s) => s.shipment.assignedToDriverApp === true)
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 bg-gray-50 border-b border-gray-100"
          ></div>
        ))}
      </div>
    );
  }

  const noData = t("table.cell.noData");

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] overflow-y-scroll">
      <table className="min-w-full border-collapse border border-gray-200">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-1">
          <tr>
            {[
              "id",
              "truck",
              "trailer",
              "driver",
              "status",
              "ref",
              "type",
              "pickup",
              "pickupDate",
              "delivery",
              "deliveryDate",
              "weight",
              "actions",
            ].map((col) => (
              <th
                key={col}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200"
              >
                {t(`table.columns.${col}`)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="bg-white">
          {groups.map((group) => {
            const isExpanded = expandedGroups.has(group._id);
            const hasShipments = group.shipments && group.shipments.length > 0;

            return (
              <React.Fragment key={group._id}>
                {/* Group Row */}
                <tr className="hover:bg-gray-50 transition-colors border-b border-l-4 border-gray-300">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                    <div className="flex items-center space-x-2">
                      {hasShipments && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(group._id);
                          }}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-600 transition-transform duration-200" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-600 transition-transform duration-200" />
                          )}
                        </button>
                      )}
                      <span>{group.groupNumber}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {group.truckLicensePlate
                      ? vehicleLabel(group.truckLicensePlate)
                      : noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {group.trailerLicensePlate
                      ? vehicleLabel(group.trailerLicensePlate)
                      : noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {group.assignedDriver
                      ? `${group.assignedDriver.firstName} ${group.assignedDriver.lastName}`
                      : noData}

                    {group.assignedDriver && (
                      <>
                        {" / "}
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            isAssignedToDriverApp(group.shipments)
                              ? "bg-purple-100 text-purple-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {isAssignedToDriverApp(group.shipments)
                            ? "Assigned"
                            : "Not Assigned"}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    {noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    {noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                    {noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {t("table.cell.multiple")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {t("table.cell.multiple")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {noData}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                    {getGroupTotalWeight(group) > 0
                      ? `${getGroupTotalWeight(group)} ${t("table.cell.weightUnit")}`
                      : 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                    <div className="flex items-center justify-end">
                      <ActionMenu
                        ariaLabel={t("table.columns.actions")}
                        items={
                          [
                            {
                              label: t("actions.edit"),
                              icon: <Eye className="w-4 h-4" />,
                              onClick: () => onEdit(group),
                            },
                            {
                              label: t("actions.viewManifest"),
                              icon: <FileDown className="w-4 h-4" />,
                              onClick: () => handlePreviewManifest(group),
                            },
                            {
                              label: t("actions.delete"),
                              icon: <Trash2 className="w-4 h-4" />,
                              onClick: () => onDelete(group._id),
                              variant: "danger",
                              divider: true,
                            },
                          ] as ActionMenuItem[]
                        }
                      />
                    </div>
                  </td>
                </tr>

                {/* Shipments Rows */}
                {isExpanded &&
                  hasShipments &&
                  group.shipments.map((groupShipment) => {
                    const shipment =
                      typeof groupShipment.shipment === "object"
                        ? groupShipment.shipment
                        : null;
                    if (!shipment) return null;

                    return (
                      <tr
                        key={`${group._id}-${shipment._id}`}
                        className="bg-blue-50 hover:bg-blue-100 transition-colors border-l-4 border-gray-200"
                      >
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-200">
                          <div className="flex items-center space-x-2 pl-8">
                            {shipment.shipmentNumber}
                          </div>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment?.carrier?.truckLicensePlate &&
                          typeof shipment.carrier.truckLicensePlate === "object"
                            ? vehicleLabel(shipment.carrier.truckLicensePlate)
                            : noData}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment?.carrier?.trailerLicensePlate &&
                          typeof shipment.carrier.trailerLicensePlate ===
                            "object"
                            ? vehicleLabel(shipment.carrier.trailerLicensePlate)
                            : noData}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment?.assignedDriver &&
                          typeof shipment.assignedDriver === "object"
                            ? `${shipment.assignedDriver.firstName} ${shipment.assignedDriver.lastName}`
                            : noData}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap border border-gray-200">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getShipmentStatusColor(
                              shipment.status,
                            )}`}
                          >
                            {shipment.status === "AwaitingPickup"
                              ? t("table.status.AwaitingPickup")
                              : shipment.status}
                          </span>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment.referenceNumber || noData}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                              shipment.type,
                            )}`}
                          >
                            {shipment.type || noData}
                          </span>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          <div>
                            <div className="font-medium">
                              {shipment?.pickup?.companyName || noData}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {toSentenceCase(
                                (
                                  shipment?.pickup?.address?.street?.split(",")[0] ||
                                  shipment?.pickup?.address?.city ||
                                  shipment?.pickup?.address?.state ||
                                  shipment?.pickup?.address?.postalCode ||
                                  ""
                                )?.toLowerCase() ||
                                  t("table.cell.addressNotAvailable"),
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment?.pickup?.date
                            ? formatDate(shipment.pickup.date)
                            : noData}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          <div>
                            <div className="font-medium">
                              {shipment?.delivery?.companyName || noData}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {toSentenceCase(
                                (
                                  shipment?.delivery?.address?.street?.split(",")[0] ||
                                  shipment?.delivery?.address?.city ||
                                  shipment?.delivery?.address?.state ||
                                  shipment?.delivery?.address?.postalCode ||
                                  ""
                                )?.toLowerCase() ||
                                  t("table.cell.addressNotAvailable"),
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment?.delivery?.date
                            ? formatDate(shipment.delivery.date)
                            : noData}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                          {shipment.totalWeight
                            ? `${shipment.totalWeight} ${t("table.cell.weightUnit")}`
                            : 0}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                          <button
                            onClick={() => handleShipmentClick(shipment._id)}
                            className="text-gray-500 hover:text-blue-900 transition-colors"
                            title={t("actions.edit")}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {groups.length === 0 && !loading && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("table.empty.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("table.empty.description")}
          </p>
        </div>
      )}

      {isManifestPreviewOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("manifest.previewTitle")}
              </h2>
              <button
                onClick={() => {
                  setIsManifestPreviewOpen(false);
                  if (manifestPreviewUrl) {
                    URL.revokeObjectURL(manifestPreviewUrl);
                    setManifestPreviewUrl(null);
                  }
                  setPreviewGroupId(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {manifestPreviewUrl ? (
                <iframe
                  src={manifestPreviewUrl}
                  className="w-full h-full border-0"
                  title={t("manifest.previewTitle")}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
